import MarketingLayout from '../layouts/MarketingLayout.jsx'
import SEOHead from '../components/SEOHead.jsx'
import CTAButton from '../components/CTAButton.jsx'
import FAQAccordion from '../components/FAQAccordion.jsx'
import { COMPARISON_ROWS, WHEN_TO_CHOOSE, FAQ_COMPARISON } from '../data/comparison.js'

const TOOLS = ['kartquiz', 'kahoot', 'quizizz', 'wooclap', 'blooket']
const TOOL_LABELS = {
  kartquiz: 'KartQuiz',
  kahoot:   'Kahoot',
  quizizz:  'Quizizz',
  wooclap:  'Wooclap',
  blooket:  'Blooket',
}

function Badge({ val }) {
  if (val === true || val?.value === true)
    return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 font-semibold text-sm">✓</span>
  if (val === false || val?.value === false)
    return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-400 font-semibold text-sm">—</span>
  return <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-600 font-semibold text-sm">~</span>
}

function CellContent({ val }) {
  const label = typeof val === 'object' ? val?.label : val
  if (label === true || label === false) return <Badge val={label} />
  return (
    <div className="flex flex-col items-center gap-1">
      <Badge val={val} />
      {label && typeof label === 'string' && (
        <span className="text-xs text-slate-500 text-center leading-tight">{label}</span>
      )}
    </div>
  )
}

function ComparisonTable() {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-6 py-4 font-display font-semibold text-kart-night w-40 md:w-56">
              Critère
            </th>
            {TOOLS.map(t => (
              <th
                key={t}
                className={`px-4 py-4 font-display font-semibold text-center ${
                  t === 'kartquiz'
                    ? 'text-kart-action bg-kart-action/5'
                    : 'text-slate-700'
                }`}
              >
                {TOOL_LABELS[t]}
                {t === 'kartquiz' && (
                  <span className="ml-1.5 text-xs bg-kart-action text-white px-1.5 py-0.5 rounded-full font-medium">
                    Nous
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARISON_ROWS.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-slate-100 last:border-0 ${i % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}
            >
              <td className="px-6 py-4 font-medium text-slate-700">{row.criterion}</td>
              {TOOLS.map(t => (
                <td
                  key={t}
                  className={`px-4 py-4 text-center ${t === 'kartquiz' ? 'bg-kart-action/5' : ''}`}
                >
                  <CellContent val={row[t]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WhenToChoose() {
  return (
    <section className="py-20 md:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night text-center mb-16">
          Quand choisir quoi
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHEN_TO_CHOOSE.map(item => (
            <div
              key={item.tool}
              className={`rounded-2xl p-6 border-2 ${
                item.highlight
                  ? 'border-kart-action bg-white shadow-lg ring-4 ring-kart-action/10'
                  : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-2.5 mb-4">
                <span className="text-2xl">{item.emoji}</span>
                <h3 className={`font-display font-semibold text-lg ${item.highlight ? 'text-kart-action' : 'text-kart-night'}`}>
                  Choisis {item.tool} si…
                </h3>
              </div>
              <ul className="space-y-2">
                {item.reasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className={`mt-1 shrink-0 ${item.highlight ? 'text-kart-action' : 'text-slate-400'}`}>•</span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const comparisonArticleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'KartQuiz vs Kahoot, Quizizz, Wooclap, Blooket : le comparatif',
  author: { '@type': 'Organization', name: 'BCOMING.Digital' },
  datePublished: '2026-05-01',
  dateModified: '2026-05-01',
}

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_COMPARISON.map(item => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
}

export default function ComparisonPage() {
  return (
    <MarketingLayout>
      <SEOHead
        title="KartQuiz vs Kahoot, Quizizz, Wooclap : le comparatif"
        description="Compare KartQuiz aux autres outils de quiz interactifs. Tableau détaillé, prix, fonctionnalités, cas d'usage. Honnête."
        canonical="/comparatif"
        ogImage="/images/og-comparatif.svg"
        jsonLd={[comparisonArticleJsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <section className="bg-[#F8FAFC] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-display font-semibold text-4xl md:text-5xl text-kart-night mb-6">
            KartQuiz vs Kahoot, Quizizz, Wooclap, Blooket : le comparatif
          </h1>
          <p className="text-slate-600 text-xl max-w-2xl mx-auto">
            Tu hésites entre plusieurs outils de quiz pour ta classe ? Voici un comparatif honnête. KartQuiz n'est pas toujours le bon choix — voici quand il l'est.
          </p>
          <p className="text-slate-400 text-xs mt-4">Mis à jour en mai 2026</p>
        </div>
      </section>

      {/* Quick answer block — crucial pour GEO */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-kart-blue/5 border-l-4 border-kart-blue rounded-r-xl p-6">
            <p className="text-sm font-semibold text-kart-blue uppercase tracking-wide mb-2">En résumé</p>
            <p className="text-slate-700 leading-relaxed">
              KartQuiz est l'outil de quiz interactif qui combine course visuelle, bonus d'attaque entre joueurs, génération de quiz par IA et synthèse pédagogique détaillée. C'est une alternative française à Kahoot, Quizizz, Wooclap et Blooket, conçue pour les profs et formateurs en collège, lycée et enseignement supérieur. Contrairement à Kahoot (linéaire, joueurs isolés) ou Blooket (trop enfantin pour le secondaire), KartQuiz propose une mécanique de course multi-joueurs avec des bonus d'attaque et de défense. Le plan bêta est gratuit.
            </p>
          </div>
        </div>
      </section>

      {/* Tableau comparatif */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display font-semibold text-2xl md:text-3xl text-kart-night mb-10 text-center">
            Tableau comparatif
          </h2>
          <ComparisonTable />
          <div className="mt-6 flex gap-6 justify-center text-xs text-slate-500 flex-wrap">
            <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 font-semibold flex items-center justify-center text-[10px]">✓</span> Inclus / natif</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-amber-100 text-amber-600 font-semibold flex items-center justify-center text-[10px]">~</span> Partiel ou payant</span>
            <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-slate-100 text-slate-400 font-semibold flex items-center justify-center text-[10px]">—</span> Non disponible</span>
          </div>
        </div>
      </section>

      <WhenToChoose />

      {/* FAQ comparative */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display font-semibold text-2xl md:text-3xl text-kart-night mb-10 text-center">
            Questions fréquentes
          </h2>
          <div className="bg-[#F8FAFC] rounded-2xl border border-slate-200 px-8 py-2">
            <FAQAccordion items={FAQ_COMPARISON} />
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-20 bg-kart-night text-white text-center">
        <div className="max-w-2xl mx-auto px-6 space-y-6">
          <h2 className="font-display font-semibold text-3xl">Tu as fait ton choix ?</h2>
          <p className="text-slate-300 text-lg">
            Crée ton compte KartQuiz gratuitement et lance ta première course en moins de 5 minutes.
          </p>
          <CTAButton to="/login" size="lg">Créer un compte gratuit</CTAButton>
        </div>
      </section>
    </MarketingLayout>
  )
}
